import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "./button";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

interface TreeSelectProps {
	readonly value: string;
	readonly onChange: Setter<string>;
	readonly placeholder?: string;
	readonly data: TreeNode[];
}
export function TreeSelect(props: TreeSelectProps) {
	const { value, onChange, data, placeholder } = props;
	const popoverButtonRef = useRef<HTMLButtonElement>(null);


	return (
		<>
			<input
				value={value}
				onChange={() => { /* suppress React warning */ }}
				style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
				onFocus={() => popoverButtonRef.current?.focus()}
			/>
			<PopoverSelect
				data={data}
				placeholder={placeholder}
				value={value}
				onSelect={onChange}
				ref={popoverButtonRef}
			/>
		</>
	);
}
export interface TreeNode {
	id: string;
	label: string;
	children?: TreeNode[];
}

interface PopoverSelectProps {
	readonly placeholder?: string;
	readonly data: TreeNode[];

	readonly value: string;
	readonly onSelect: Setter<string>;
	readonly ref?: React.Ref<HTMLButtonElement>;
}
function PopoverSelect(props: PopoverSelectProps) {
	const { onSelect, data, value, ref, placeholder } = props;
	const [open, setOpen] = useState(false);

	const [currentView, setCurrentView] = useState<TreeNode | null>(null);

	const mappedData = useMemo(() => {
		const fullMap = new Map<string, TreeNode>();
		const traverse = (node: TreeNode) => {
			if (fullMap.has(node.id)) {
				throw new Error(`Duplicate node id found: ${node.id}`);
			}

			fullMap.set(node.id, node);
			if (node.children) {
				node.children.forEach(traverse);
			}
		};
		data.forEach(traverse);
		return fullMap;
	}, [data]);

	const invertedTree = useMemo(() => {
		const inverted = new Map<string, string | null>();
		const traverse = (node: TreeNode, parentId: string | null) => {
			inverted.set(node.id, parentId);
			if (node.children) {
				node.children.forEach((child) => traverse(child, node.id));
			}
		};
		data.forEach((node) => traverse(node, null));
		return inverted;
	}, [data]);


	useLayoutEffect(() => {
		const selectedNode = invertedTree.get(value);
		const parentNode = selectedNode ? mappedData.get(selectedNode) : null;
		if (open && value && parentNode) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setCurrentView(parentNode);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- We only want this to load on open, to set the current path.
	}, [open]);



	const currentValue = mappedData.get(value) ?? null;

	const breadcrumb = useMemo(() => {
		const path = [];
		let current = currentView;
		if (current == null) return [];

		for (let i = 0; i < 100; i++) { // safety limit to prevent infinite loops
			path.unshift(current);
			const parentId = invertedTree.get(current.id);
			if (parentId == undefined) break; //We reached the root of the tree

			const parent = mappedData.get(parentId);
			if (parent == undefined) break; //Something broke spectacularly

			current = parent;
		}

		return path;
	}, [currentView, invertedTree, mappedData]);



	const goBack = useCallback(() => {
		if (currentView === null) return;

		const parentId = invertedTree.get(currentView.id);
		if (!parentId) {
			setCurrentView(null);
		} else {
			const parentNode = mappedData.get(parentId);
			if (parentNode) {
				setCurrentView(parentNode);
			}
		}
	}, [currentView, invertedTree, mappedData]);


	const handleSelect = useCallback((node: TreeNode) => {
		onSelect(node.id);
		setOpen(false);
	}, [onSelect]);

	const shownNodes = currentView ? currentView.children ?? [] : data;
	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="input"
					className="pr-1"
					role="combobox"
					type="button"
					ref={ref}
					aria-expanded={open}
					aria-haspopup="listbox"
				>
					{currentValue?.label ?? placeholder ?? "Select something"}
					<ChevronDown />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-100 max-h-[400px] flex flex-col"
				align="start"
			>
				<Breadcrumb breadcrumb={breadcrumb} setView={setCurrentView} />
				{open && (
					<div className="overflow-y-auto overflow-x-hidden">
						<AnimatePresence mode="wait" initial={false}>
							<motion.ul
								key={breadcrumb.map(node => node.id).join("-")}
								initial={{ opacity: 0, x: 10 }}
								animate={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0, x: -10 }}
								transition={{ duration: 0.15 }}
								className="space-y-1"
							>
								{(currentView != null) && (
									<li>
										<button className="w-full text-left px-2 py-1 hover:bg-muted rounded" onClick={goBack}>
											🔙 Back
										</button>
									</li>
								)}

								{shownNodes.map((node) => (
									<SelectItem
										key={node.id}
										node={node}
										handleNavigate={setCurrentView}
										handleSelect={handleSelect}
									/>
								))}
							</motion.ul>
						</AnimatePresence>
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}



interface BreadcrumbProps {
	readonly breadcrumb: TreeNode[];
	readonly setView: Setter<TreeNode | null>;
}
function Breadcrumb(props: BreadcrumbProps) {
	const excludingLast = [...props.breadcrumb];
	const lastNode = excludingLast.pop();

	if (lastNode == null) return (
		<div className="text-sm mb-2">
			<div className="text-muted-foreground">Root</div>
		</div>
	);

	return (
		<div className="text-sm mb-2">
			<div className="flex gap-1 flex-wrap">
				<button onClick={() => props.setView(null)} className="underline">
					Root
				</button>

				{excludingLast.map((node) => (
					<span key={node.id}>
						{"/ "}
						<button onClick={() => props.setView(node)} className="underline">
							{node.label}
						</button>
					</span>
				))}

				<span className="text-muted-foreground">
					{"/ "}
					{lastNode.label}
				</span>
			</div>
		</div>
	);
}


interface SelectItemProps {
	readonly node: TreeNode;
	readonly handleNavigate: (to: TreeNode) => void;
	readonly handleSelect: (value: TreeNode) => void;
}
function SelectItem(props: SelectItemProps) {
	const { node, handleNavigate, handleSelect } = props;

	return (
		<li>
			<button
				className="w-full text-left px-2 py-1 hover:bg-muted rounded"
				onClick={() => {
					if (node.children) {
						handleNavigate(node);
					} else {
						handleSelect(node);
					}
				}}
			>
				{node.children ? "📁" : "✅"} {node.label}
			</button>
		</li>
	);
}
